import type { CasinoNewGames } from '@ps/web-martech-cms-types';
import classNames from 'clsx';
import { camelCase } from 'lodash';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { CasinoGameTile } from '@swf/casino';
import type {
  CasinoGameTileProps,
  CasinoIcon,
  FeatureVariant,
  MarketingIconType
} from '@swf/casino';
import { select } from '@swf/core/store';
import type { CasinoGame } from '@swf/core/types/CasinoGame';
import type { CasinoGameList } from '@swf/core/types/CasinoGameList';
import { getTablePropsFromGame, isGameInfoAvailable } from '@swf/core/utils/casino';
import getBackgroundImageStyle from '@swf/core/utils/getBackgroundImageStyle';

import GameFeatures from '../CasinoGameInfoCard/components/GameFeatures/GameFeatures';
import GameTrailerIcon from '../GameTrailerIcon/GameTrailerIcon';

import styles from './GameInfoTile.scss';

export interface Props {
  classes?: {
    title?: string;
    infoIcon?: string;
    tile?: string;
    infoBackground?: string;
    overlayBackground?: string;
    jackpot?: CasinoGameTileProps['jackpotClassName'];
  };
  variant?: 'carousel' | CasinoGameList['variant'];
  featureVariant?: FeatureVariant[];
  launchSource?: string;
  count?: number;
  index?: number;
  game: CasinoGame;
  favouriteIcon?: JSX.Element;
  showFields?: CasinoGameList['renderInfo'];
  icon?: CasinoIcon;
  trailer?: boolean;
  carouselIcon?: CasinoNewGames['carousel_icon'];
  nested?: boolean;
  shortText?: boolean;
  marketingIconTag?: MarketingIconType;
}

const GameInfoTile: React.FC<Props> = ({
  launchSource,
  classes = {},
  game,
  index,
  count,
  trailer,
  icon = 'icnSquare',
  showFields = [],
  variant,
  featureVariant,
  carouselIcon,
  nested,
  shortText
}) => {
  const isMobile = useSelector(select.device.isMobile);

  const backgroundImage =
    game.images[icon] || game.images.icnSquare || game.images.icnGameTrailerIcon;

  const infoArea = isGameInfoAvailable(game) || showFields?.length;

  const table = useMemo(() => getTablePropsFromGame(game), [game]);

  return (
    <div
      className={classNames(
        styles.container,
        infoArea && styles[camelCase(variant) as keyof typeof styles],
        nested && styles.nestedTile
      )}
    >
      {trailer && <GameTrailerIcon game={game} className={styles.icon} index={index} />}
      <div className={classNames(styles.info, !trailer && styles.endInfo)}>
        <div
          className={classNames(
            styles.infoBackground,
            carouselIcon && styles.marketingOverlay,
            classes.infoBackground
          )}
          style={getBackgroundImageStyle(backgroundImage)}
        >
          <div className={classNames(styles.infoBackgroundOverlay, classes.overlayBackground)} />
        </div>
        <CasinoGameTile
          launchSource={launchSource}
          className={classNames(styles.gameIcon, classes.tile)}
          game={game}
          infoIconClass={classes.infoIcon}
          icon={icon}
          hideFeatureButtons={isMobile}
          jackpotClassName={classes.jackpot}
        />
        <GameFeatures
          className={classNames(styles.features)}
          featureVariant={featureVariant}
          data={table}
          carouselIcon={carouselIcon}
          game={game}
          detailsOnly
          showFields={showFields}
          shortText={shortText}
        />
      </div>
    </div>
  );
};

export default GameInfoTile;
